import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { AuthContext } from "../auth/AuthContext";
import { getBudgetPortfolio, getBudgetSubmissionCurrent, getBudgetSummary } from "../api/budgetApi";
import ProjectBudgetPreviewDialog from "../components/ProjectBudgetPreviewDialog";
import BudgetScopeFilters, { FilterField, selectStyle } from "../components/BudgetScopeFilters";
import BudgetCustomRangePicker from "../components/BudgetCustomRangePicker";
import BudgetSubmissionsPanel from "../components/BudgetSubmissionsPanel";
import BudgetOrgBudgetPanel from "../components/BudgetOrgBudgetPanel";
import BudgetDepartmentBreakdown from "../components/BudgetDepartmentBreakdown";
import { getStoredBudgetOrganizationView } from "../components/BudgetOrganizationSwitcher";
import { getStoredDepartmentView } from "../components/DepartmentSwitcher";
import { formatInr } from "../utils/formatInr";
import { isHodTier, isSuperAdminTier } from "../constants/orgTiers";
import { BUDGET_PLAN_SCOPE } from "../constants/budgetPlanScope";
import { COLORS as C, RADIUS } from "../constants/ui";

const currentYear = () => new Date().getFullYear();

const PERIOD_OPTIONS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "year", label: "Full year" },
  { value: "custom", label: "Custom range" },
];

/**
 * Scales the value down as it gets longer, so a big rupee figure stays on one
 * line instead of either overflowing the card or breaking mid-digit. Short
 * values (counts, "₹0") are unaffected — they're nowhere near these lengths.
 * Tuned against the card's own minimum width (180px, minus 40px padding).
 */
function summaryValueFontSize(value) {
  const len = String(value ?? "").length;
  if (len <= 9) return "1.55rem"; // e.g. "₹99,999" — the size this always was
  if (len <= 12) return "1.2rem"; // e.g. "₹1,00,50,000"
  if (len <= 15) return "1rem";
  return "0.85rem";
}

function SummaryCard({ label, value, hint, accent = C.accentInk }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        border: `1px solid ${C.line}`,
        padding: "18px 20px",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        // A grid item's default min-width is auto, which lets its content's
        // intrinsic width push the card wider than its track instead of
        // shrinking to fit. Needed alongside the font scaling below for
        // values so long even the smallest tier would still be tight.
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: C.inkSoft, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div
        style={{
          fontSize: summaryValueFontSize(value),
          fontWeight: 800,
          color: accent,
          marginTop: 6,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={String(value ?? "")}
      >
        {value}
      </div>
      {hint ? <div style={{ fontSize: "0.78rem", color: C.inkFaint, marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}

function UtilizationBar({ pct, overBudget, hasSpendWithoutBudget }) {
  const width = pct == null ? 0 : Math.min(100, Math.max(0, pct));
  const color = overBudget ? C.danger : width > 85 ? C.warn : C.accent;
  const label =
    pct != null ? `${pct}%` : hasSpendWithoutBudget ? "No budget set" : "—";
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: C.inkSoft, marginBottom: 4 }}>
        <span style={hasSpendWithoutBudget ? { color: "#92400e", fontWeight: 600 } : undefined}>{label}</span>
        {overBudget ? <span style={{ color: C.danger, fontWeight: 700 }}>Over</span> : null}
      </div>
      <div style={{ height: 8, borderRadius: 999, background: C.line, overflow: "hidden" }}>
        <div style={{ width: `${width}%`, height: "100%", background: color, borderRadius: 999 }} />
      </div>
    </div>
  );
}


function defaultCustomRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmt(start), end: fmt(end) };
}

export default function Budget() {
  const { role, user } = useContext(AuthContext);
  const qc = useQueryClient();
  const currentRole = (role || "").toLowerCase();
  const isSuper = isSuperAdminTier(currentRole);
  const isHod = isHodTier(currentRole);
  const [year, setYear] = useState(currentYear());
  const [period, setPeriod] = useState("year");
  const [customRange, setCustomRange] = useState(defaultCustomRange);
  const [preview, setPreview] = useState(null);
  // Deep link from a budget notification: ?submissionId=42
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSubmissionId = searchParams.get("submissionId");
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(
    urlSubmissionId ? Number(urlSubmissionId) : null
  );
  const [organizationView, setOrganizationView] = useState(() => getStoredBudgetOrganizationView());
  const [departmentView, setDepartmentView] = useState(() => getStoredDepartmentView() || "");

  const approvalDepartment = departmentView || user?.department || "";
  const submissionYear = period === "year" ? year : currentYear();

  const submissionQuery = useQuery({
    queryKey: ["budget-submission-current", selectedSubmissionId, approvalDepartment, submissionYear, organizationView],
    queryFn: () =>
      getBudgetSubmissionCurrent({
        id: selectedSubmissionId || undefined,
        department: selectedSubmissionId ? undefined : approvalDepartment,
        year: submissionYear,
        organizationView: organizationView || undefined,
      }).then((r) => r.data),
    enabled: Boolean(approvalDepartment || selectedSubmissionId) && (isHod || isSuper),
    staleTime: 0,
  });

  const canEdit = useMemo(() => {
    if (isSuper) return true;
    if (!isHod || !selectedSubmissionId) return false;
    const sub = submissionQuery.data?.submission;
    if (!sub || sub.id !== selectedSubmissionId) return false;
    return sub.status === "draft" || sub.status === "rejected";
  }, [isSuper, isHod, selectedSubmissionId, submissionQuery.data]);

  const activeSubmission = submissionQuery.data?.submission;
  const isEditingDraftPlan =
    isHod &&
    !isSuper &&
    activeSubmission &&
    activeSubmission.id === selectedSubmissionId &&
    (activeSubmission.status === "draft" || activeSubmission.status === "rejected");

  useEffect(() => {
    if (isHod && !isSuper && !departmentView && user?.department) {
      setDepartmentView(user.department);
    }
  }, [isHod, isSuper, departmentView, user?.department]);

  // Consume the deep link once, then drop it so a refresh does not keep
  // forcing the same submission open.
  useEffect(() => {
    if (!urlSubmissionId) return;
    setSelectedSubmissionId(Number(urlSubmissionId));
    setSearchParams({}, { replace: true });
  }, [urlSubmissionId, setSearchParams]);

  useEffect(() => {
    const onOrgChange = (e) => setOrganizationView(e.detail || "");
    const onDeptChange = (e) => setDepartmentView(e.detail || "");
    window.addEventListener("budgetOrganizationViewChanged", onOrgChange);
    window.addEventListener("departmentViewChanged", onDeptChange);
    return () => {
      window.removeEventListener("budgetOrganizationViewChanged", onOrgChange);
      window.removeEventListener("departmentViewChanged", onDeptChange);
    };
  }, []);

  const portfolioParams = useMemo(
    () => ({
      year: period === "year" ? year : undefined,
      organizationView: organizationView || undefined,
      period,
      startDate: period === "custom" ? customRange.start : undefined,
      endDate: period === "custom" ? customRange.end : undefined,
    }),
    [year, organizationView, period, customRange]
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["budget-portfolio", portfolioParams, departmentView],
    queryFn: () => getBudgetPortfolio(portfolioParams).then((r) => r.data),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const refetchPortfolio = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["budget-portfolio"] });
    qc.invalidateQueries({ queryKey: ["budget-submissions"] });
    qc.invalidateQueries({ queryKey: ["budget-submission-current"] });
    qc.invalidateQueries({ queryKey: ["budget-submission-detail"] });
    qc.invalidateQueries({ queryKey: ["budget-summary"] });
    qc.invalidateQueries({ queryKey: ["budget-org-budget"] });
  }, [qc]);

  const summaryYear = period === "year" ? year : currentYear();
  const summaryDept =
    isHod && !isSuper ? departmentView || user?.department || undefined : departmentView || undefined;
  const summaryQuery = useQuery({
    queryKey: ["budget-summary", summaryYear, organizationView, summaryDept, isSuper, isHod],
    queryFn: () =>
      getBudgetSummary({
        year: summaryYear,
        organizationView: organizationView || undefined,
        departmentView: summaryDept,
      }).then((r) => r.data),
    enabled: (isSuper || isHod) && Boolean(isSuper || summaryDept),
    staleTime: 0,
  });

  const budgetSummary = summaryQuery.data;

  const projects = data?.projects || [];
  const summary = data?.summary || {
    projectCount: 0,
    totalAllocated: 0,
    ytdSpend: 0,
    currentMonthSpend: 0,
    overBudgetCount: 0,
    approvedAllocationCount: 0,
  };

  const visibleProjects = useMemo(() => {
    if (
      !isEditingDraftPlan ||
      activeSubmission?.scopeType !== BUDGET_PLAN_SCOPE.PROJECT ||
      !activeSubmission?.projectId
    ) {
      return projects;
    }
    const pid = String(activeSubmission.projectId);
    return projects.filter((p) => String(p.projectId) === pid);
  }, [projects, isEditingDraftPlan, activeSubmission?.scopeType, activeSubmission?.projectId]);

  const yearOptions = useMemo(
    () => [0, 1, 2, 3, 4].map((d) => currentYear() - d),
    []
  );

  const periodLabel = data?.periodLabel || "Year";
  const spendColumnLabel = period === "year" ? `YTD ${data?.year || year}` : "Period spend";

  const scopeHint = useMemo(() => {
    const parts = [periodLabel];
    if (organizationView) parts.push(organizationView);
    if (departmentView) parts.push(departmentView);
    else parts.push("All departments");
    return parts.join(" · ");
  }, [periodLabel, organizationView, departmentView]);

  const tableHeaders = useMemo(() => {
    const base = ["Project", "Department"];
    if (!organizationView) base.push("Organisation");
    return [...base, "Allocated", spendColumnLabel, period === "year" ? "This month" : "In period", "Utilization", ""];
  }, [organizationView, spendColumnLabel, period]);

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div style={{ flex: "1 1 280px", minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: "1.65rem", fontWeight: 800, color: C.ink }}>Budget</h1>
          <p style={{ margin: "6px 0 0", color: C.inkSoft, fontSize: "0.92rem", maxWidth: 520 }}>
            Portfolio spend against yearly allocation. Use filters on the right for period, organisation, and department.
          </p>
          <p style={{ margin: "4px 0 0", color: C.accentInk, fontSize: "0.8rem", fontWeight: 700 }}>
            Viewing: {scopeHint}
          </p>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", justifyContent: "flex-end" }}>
          <FilterField label="Period">
            <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ ...selectStyle, minWidth: 130 }}>
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FilterField>

          {period === "year" ? (
            <FilterField label="Year">
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ ...selectStyle, minWidth: 90 }}>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </FilterField>
          ) : null}

          {period === "custom" ? (
            <FilterField label="Date range">
              <BudgetCustomRangePicker
                start={customRange.start}
                end={customRange.end}
                onChange={setCustomRange}
              />
            </FilterField>
          ) : null}

          <BudgetScopeFilters
            organizationView={organizationView}
            onOrganizationChange={setOrganizationView}
          />
        </div>
      </div>

      <BudgetSubmissionsPanel
        role={currentRole}
        department={approvalDepartment}
        budgetYear={submissionYear}
        organizationView={organizationView}
        departmentProjects={projects}
        selectedSubmissionId={selectedSubmissionId}
        onSelectSubmission={setSelectedSubmissionId}
        onBudgetChange={refetchPortfolio}
        portfolioTotalAllocated={summary.totalAllocated}
      />

      {isSuper && period === "year" ? (
        <BudgetOrgBudgetPanel
          budgetYear={year}
          organizationView={organizationView}
          onSaved={refetchPortfolio}
        />
      ) : null}

      {isSuper && !departmentView && budgetSummary?.role === "superadmin" ? (
        <BudgetDepartmentBreakdown departments={budgetSummary.departments || []} />
      ) : null}

      {isLoading ? (
        <div style={{ padding: "2rem 0", color: C.inkSoft }}>Loading budget portfolio…</div>
      ) : isError ? (
        <div style={{ padding: "1rem", borderRadius: 12, background: C.dangerSoft, color: "#b91c1c" }}>
          Could not load budget data. You may not have access to this view.
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 14,
              marginBottom: 20,
            }}
          >
            {isSuper && budgetSummary?.role === "superadmin" ? (
              <>
                <SummaryCard
                  label="Total org budget"
                  value={formatInr(budgetSummary.totalOrgBudget)}
                  accent="#2C63AE"
                  hint={organizationView || "All organisations"}
                />
                <SummaryCard
                  label="Total allocated"
                  value={formatInr(budgetSummary.totalAllocated)}
                  accent="#0f766e"
                  hint="Approved + pending dept plans"
                />
                <SummaryCard
                  label="Remaining org budget"
                  value={
                    budgetSummary.remainingOrgBudget != null
                      ? formatInr(budgetSummary.remainingOrgBudget)
                      : "—"
                  }
                  accent="#0369a1"
                />
                <SummaryCard
                  label={`YTD spend (${data?.year || year})`}
                  value={formatInr(budgetSummary.ytdSpend ?? summary.ytdSpend)}
                  accent={C.accentInk}
                />
                <SummaryCard
                  label="Over-budget depts"
                  value={budgetSummary.overBudgetDeptCount ?? 0}
                  accent={budgetSummary.overBudgetDeptCount > 0 ? C.danger : C.inkSoft}
                />
              </>
            ) : isHod && budgetSummary?.role === "hod" ? (
              <>
                <SummaryCard
                  label="Dept total budget"
                  value={formatInr(budgetSummary.deptTotalBudget)}
                  accent="#2C63AE"
                  hint={budgetSummary.department}
                />
                <SummaryCard
                  label="Allocated to projects"
                  value={formatInr(budgetSummary.allocatedToProjects)}
                  accent="#0f766e"
                />
                <SummaryCard
                  label="Common dept spend"
                  value={formatInr(budgetSummary.commonDeptSpend)}
                  accent="#7c3aed"
                />
                <SummaryCard
                  label={`YTD spend (${data?.year || year})`}
                  value={formatInr(budgetSummary.ytdSpend ?? summary.ytdSpend)}
                  accent={C.accentInk}
                />
                <SummaryCard
                  label="Over-budget projects"
                  value={budgetSummary.overBudgetProjectCount ?? summary.overBudgetCount}
                  accent={(budgetSummary.overBudgetProjectCount ?? 0) > 0 ? C.danger : C.inkSoft}
                />
                <SummaryCard
                  label="Pending plans"
                  value={budgetSummary.pendingPlanCount ?? 0}
                  accent={C.warn}
                  hint="Draft / pending / rejected"
                />
                <SummaryCard
                  label="Pending expense claims"
                  value={budgetSummary.pendingExpenseApprovals ?? 0}
                  accent="#c2410c"
                  hint="Awaiting HOD action — see Expense Claims"
                />
              </>
            ) : (
              <>
                <SummaryCard label="Projects" value={summary.projectCount} hint="Active portfolio in view" />
                <SummaryCard
                  label="Total allocated"
                  value={formatInr(summary.totalAllocated)}
                  accent="#0f766e"
                  hint={summary.approvedAllocationCount > 0 ? `${summary.approvedAllocationCount} project(s) with approved budget` : isSuper || isHod ? "From approved budget plans" : undefined}
                />
                <SummaryCard
                  label={period === "year" ? `YTD spend (${data?.year || year})` : "Period spend"}
                  value={formatInr(summary.ytdSpend)}
                  accent={C.accentInk}
                  hint={organizationView ? `${organizationView} spends only` : undefined}
                />
                {period === "year" ? (
                  <SummaryCard label="This month" value={formatInr(summary.currentMonthSpend)} accent="#7c3aed" />
                ) : null}
                <SummaryCard
                  label="Over budget"
                  value={summary.overBudgetCount}
                  hint="Projects above allocation"
                  accent={summary.overBudgetCount > 0 ? C.danger : C.inkSoft}
                />
              </>
            )}
          </div>

          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              border: `1px solid ${C.line}`,
              overflow: "hidden",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
            }}
          >
            <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.line}` }}>
              <div style={{ fontWeight: 800, color: C.ink }}>Projects</div>
              <div style={{ fontSize: "0.8rem", color: C.inkSoft, marginTop: 2 }}>
                {isHod && !isSuper && !isEditingDraftPlan
                  ? "Select a draft or rejected budget plan above to edit project allocations."
                  : isEditingDraftPlan && activeSubmission?.scopeType === BUDGET_PLAN_SCOPE.PROJECT
                    ? `Showing project in scope: ${activeSubmission.projectName || activeSubmission.projectId}`
                    : organizationView || period !== "year"
                      ? "Logged spends for current filters — recurring run-rate shown only for full-year view"
                      : "Recurring lines + logged spends (INR)"}
              </div>
            </div>

            {visibleProjects.length === 0 ? (
              <div style={{ padding: "2.5rem 1.5rem", textAlign: "center", color: C.inkSoft }}>
                No projects match these filters.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
                  <thead>
                    <tr style={{ background: C.sunken, textAlign: "left" }}>
                      {tableHeaders.map((h) => (
                        <th key={h || "actions"} style={{ padding: "12px 14px", fontSize: "0.72rem", color: C.inkSoft, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProjects.map((row) => (
                      <tr key={row.projectId} style={{ borderTop: `1px solid ${C.sunken}` }}>
                        <td style={{ padding: "14px", fontWeight: 700, color: C.ink }}>{row.projectName}</td>
                        <td style={{ padding: "14px", color: C.inkSoft, fontSize: "0.88rem" }}>{row.department || "—"}</td>
                        {!organizationView ? (
                          <td style={{ padding: "14px", color: C.inkSoft, fontSize: "0.88rem" }}>
                            {row.organizations?.length ? row.organizations.join(", ") : "—"}
                          </td>
                        ) : null}
                        <td style={{ padding: "14px", whiteSpace: "nowrap" }}>
                          <span
                            style={{
                              fontWeight: row.approvedAllocation ? 800 : 400,
                              color: row.approvedAllocation ? "#0f766e" : C.ink,
                            }}
                          >
                            {formatInr(row.budgetAllocated)}
                          </span>
                          {row.approvedAllocation ? (
                            <span
                              style={{
                                display: "inline-block",
                                marginLeft: 6,
                                padding: "2px 6px",
                                borderRadius: 999,
                                fontSize: "0.62rem",
                                fontWeight: 800,
                                background: C.doneSoft,
                                color: "#15803d",
                                verticalAlign: "middle",
                              }}
                            >
                              APPROVED
                            </span>
                          ) : null}
                        </td>
                        <td style={{ padding: "14px", whiteSpace: "nowrap", fontWeight: 700, color: row.overBudget ? C.danger : C.ink }}>
                          {formatInr(row.ytdSpend)}
                        </td>
                        <td style={{ padding: "14px", whiteSpace: "nowrap" }}>
                          {formatInr(period === "year" ? (row.currentMonthSpend || 0) : row.ytdSpend)}
                        </td>
                        <td style={{ padding: "14px" }}>
                          <UtilizationBar
                            pct={row.utilizationPct}
                            overBudget={row.overBudget}
                            hasSpendWithoutBudget={(row.budgetAllocated || 0) <= 0 && (row.ytdSpend || 0) > 0}
                          />
                        </td>
                        <td style={{ padding: "14px", textAlign: "right" }}>
                          <button
                            type="button"
                            onClick={() => setPreview({
                              id: row.projectId,
                              name: row.projectName,
                              budgetAllocated: row.budgetAllocated,
                              department: row.department,
                            })}
                            style={{
                              border: "none",
                              borderRadius: 9,
                              padding: "8px 14px",
                              background: C.accentInk,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.82rem",
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {canEdit ? "Manage" : "View"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {preview ? (
        <ProjectBudgetPreviewDialog
          open
          onClose={() => {
            setPreview(null);
            refetchPortfolio();
          }}
          projectId={preview.id}
          projectName={preview.name}
          projectDepartment={preview.department}
          yearlyBudget={preview.budgetAllocated ?? 0}
          organizationView={organizationView}
          canEdit={canEdit}
          onSaved={refetchPortfolio}
        />
      ) : null}
    </div>
  );
}
